export function finiteNumberOrDefault(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export const REPORT_LOSS_PAYLOAD_DEFAULTS = {
  Soiling: 2,
  Shading: 0,
  Snow: 0,
  Mismatch: 1.5,
  Wiring: 2,
  Connections: 0.5,
  Light_Induced_Degradation: 1.5,
  Nameplate_Rating: 1,
  Age: 0,
  Availability: 1
};

const parseMonthly = (value) => {
  if (typeof value !== 'string' || !value.includes(',')) return value;
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => finiteNumberOrDefault(item, null));
};

export function normalizeMonthlyArray(value, fallback) {
  const source = parseMonthly(value);
  return Array.from({ length: 12 }, (_, index) =>
    Array.isArray(source) ? (source[index] ?? fallback) : fallback
  );
}

export function normalizeMonthlySolarAccess(value) {
  return normalizeMonthlyArray(value, 1).map((item) =>
    Math.min(1, Math.max(0, finiteNumberOrDefault(item, 1)))
  );
}

export function normalizeMonthlyLoss(value) {
  return normalizeMonthlyArray(value, 0).map((item) =>
    finiteNumberOrDefault(item, 0)
  );
}

const firstDefined = (source, keys) => {
  if (!source || typeof source !== 'object') return undefined;
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return undefined;
};

const lossNumber = (value, fallback) =>
  value === undefined || value === null || value === ''
    ? fallback
    : finiteNumberOrDefault(value, fallback);

export function buildLossPayload(losses = {}, defaults = {}) {
  const values = {
    Soiling: defaults.Soiling ?? REPORT_LOSS_PAYLOAD_DEFAULTS.Soiling,
    Shading: defaults.Shading ?? REPORT_LOSS_PAYLOAD_DEFAULTS.Shading,
    Snow: defaults.Snow ?? REPORT_LOSS_PAYLOAD_DEFAULTS.Snow,
    Mismatch: defaults.Mismatch ?? REPORT_LOSS_PAYLOAD_DEFAULTS.Mismatch,
    Wiring: defaults.Wiring ?? REPORT_LOSS_PAYLOAD_DEFAULTS.Wiring,
    Connections:
      defaults.Connections ?? REPORT_LOSS_PAYLOAD_DEFAULTS.Connections,
    Light_Induced_Degradation:
      defaults.Light_Induced_Degradation ??
      REPORT_LOSS_PAYLOAD_DEFAULTS.Light_Induced_Degradation,
    Nameplate_Rating:
      defaults.Nameplate_Rating ??
      REPORT_LOSS_PAYLOAD_DEFAULTS.Nameplate_Rating,
    Age: defaults.Age ?? REPORT_LOSS_PAYLOAD_DEFAULTS.Age,
    Availability:
      defaults.Availability ?? REPORT_LOSS_PAYLOAD_DEFAULTS.Availability
  };
  return {
    Soiling: lossNumber(
      firstDefined(losses, ['soiling', 'Soiling']),
      values.Soiling
    ),
    Shading: lossNumber(
      firstDefined(losses, ['shading', 'Shading']),
      values.Shading
    ),
    Snow: lossNumber(firstDefined(losses, ['snow', 'Snow']), values.Snow),
    Mismatch: lossNumber(
      firstDefined(losses, ['mismatch', 'Mismatch']),
      values.Mismatch
    ),
    Wiring: lossNumber(
      firstDefined(losses, ['wiring', 'Wiring']),
      values.Wiring
    ),
    Connections: lossNumber(
      firstDefined(losses, ['connections', 'Connections']),
      values.Connections
    ),
    Light_Induced_Degradation: lossNumber(
      firstDefined(losses, [
        'lid',
        'lightInducedDegradation',
        'light_induced_degradation',
        'Light_Induced_Degradation'
      ]),
      values.Light_Induced_Degradation
    ),
    Nameplate_Rating: lossNumber(
      firstDefined(losses, [
        'nameplate_rating',
        'nameplateRating',
        'Nameplate_Rating'
      ]),
      values.Nameplate_Rating
    ),
    Age: lossNumber(firstDefined(losses, ['age', 'Age']), values.Age),
    Availability: lossNumber(
      firstDefined(losses, ['availability', 'Availability']),
      values.Availability
    )
  };
}

export function buildPanelsByIndexFromPanelShadingData({
  panelShadingData,
  segmentIndex
} = {}) {
  const result = {};
  if (!Array.isArray(panelShadingData) || !panelShadingData.length) {
    return result;
  }
  const monthly = panelShadingData[panelShadingData.length - 1];
  const hasMonthly =
    monthly && typeof monthly === 'object' && !Array.isArray(monthly);
  const annualRows = hasMonthly
    ? panelShadingData.slice(0, -1)
    : panelShadingData;
  annualRows.forEach((row) => {
    if (!Array.isArray(row) || row.length < 9 || row[0] !== segmentIndex) {
      return;
    }
    if (!result[row[2]]) {
      result[row[2]] = {
        annual: {
          solar_access: row[6] ?? 1,
          tof: row[7] ?? 0,
          tsrf: row[8] ?? 0
        },
        monthly: {}
      };
    }
  });
  if (!hasMonthly) return result;
  for (let month = 1; month <= 12; month += 1) {
    const rows = Array.isArray(monthly[month]) ? monthly[month] : [];
    const byPanel = {};
    rows.forEach((row) => {
      if (Array.isArray(row) && row.length >= 9 && row[0] === segmentIndex) {
        byPanel[row[2]] = {
          solar_access: row[6] ?? 1,
          tof: row[7] ?? 0,
          tsrf: row[8] ?? 0
        };
      }
    });
    Object.keys(result).forEach((panelIndex) => {
      result[panelIndex].monthly[month] = byPanel[panelIndex] || {
        solar_access: 1,
        tof: 0,
        tsrf: 0
      };
    });
  }
  return result;
}

export function buildPerPanelShadingArrays({
  panelIndicesInPayload = [],
  panelCount = 0,
  panelsByIndex = {},
  segmentMonthlySA = [],
  segmentMonthlyTOF = [],
  segmentMonthlyTSRF = [],
  segmentAnnualSA = 1,
  segmentAnnualTOF = 0,
  segmentAnnualTSRF = 0
} = {}) {
  const output = {
    monthly_solar_access: [],
    monthly_tof: [],
    monthly_tsrf: [],
    annual_solar_access: [],
    annual_tof: [],
    annual_tsrf: []
  };
  const resolved = Array.from({ length: panelCount }, (_, index) => {
    const panel = panelsByIndex[panelIndicesInPayload[index]];
    if (!panel) return null;
    return {
      solarAccess: normalizeMonthlySolarAccess(
        Array.from(
          { length: 12 },
          (_, month) => panel.monthly[month + 1]?.solar_access ?? 1
        )
      ),
      tof: normalizeMonthlyLoss(
        Array.from(
          { length: 12 },
          (_, month) => panel.monthly[month + 1]?.tof ?? 0
        )
      ),
      tsrf: normalizeMonthlyLoss(
        Array.from(
          { length: 12 },
          (_, month) => panel.monthly[month + 1]?.tsrf ?? 0
        )
      ),
      annualSolarAccess: finiteNumberOrDefault(panel.annual?.solar_access, 1),
      annualTof: finiteNumberOrDefault(panel.annual?.tof, 0),
      annualTsrf: finiteNumberOrDefault(panel.annual?.tsrf, 0)
    };
  });
  const hasPositiveSolarAccess = resolved.some((panel) =>
    panel?.solarAccess?.some((value) => Number(value) > 0)
  );
  resolved.forEach((panel) => {
    const value = hasPositiveSolarAccess ? panel : null;
    output.monthly_solar_access.push(
      value ? value.solarAccess : [...segmentMonthlySA]
    );
    output.monthly_tof.push(value ? value.tof : [...segmentMonthlyTOF]);
    output.monthly_tsrf.push(value ? value.tsrf : [...segmentMonthlyTSRF]);
    output.annual_solar_access.push(
      value ? value.annualSolarAccess : segmentAnnualSA
    );
    output.annual_tof.push(value ? value.annualTof : segmentAnnualTOF);
    output.annual_tsrf.push(value ? value.annualTsrf : segmentAnnualTSRF);
  });
  return output;
}
